import { isPlatformServer } from '@angular/common';
import type { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import type { Request } from 'express';
import type { ParsedQs } from 'qs';
import type { Observable } from 'rxjs';

import { REQUEST } from 'src/express.tokens';
import { EnvironmentService } from 'src/services/environment/environment.service';

@Injectable()
export class CacheFlusherInterceptor implements HttpInterceptor {
    private readonly injector = inject(Injector);
    private readonly environmentService = inject(EnvironmentService);

    private readonly QUERY_PARAMS_TO_FORWARD = ['ld'];
    private readonly HEADERS_TO_FORWARD = ['x-xwr'];
    private readonly HEADERS_TO_ADD = { 'x-req-client': 'node-angular' } as const;

    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const isServerEnv = isPlatformServer(this.injector.get(PLATFORM_ID));
        const isApiRequest = this.isApiRequest(req.url);

        if (isServerEnv && isApiRequest) {
            const expressReq: Request = this.injector.get(REQUEST);

            return next.handle(
                req.clone({
                    url: this.forwardQueryParams(req.url, expressReq.query, this.QUERY_PARAMS_TO_FORWARD),
                    setHeaders: {
                        ...this.HEADERS_TO_ADD,
                        ...this.forwardHeaders(expressReq.headers, this.HEADERS_TO_FORWARD),
                    },
                })
            );
        }

        return next.handle(req);
    }

    private forwardQueryParams(url: string, existedParams: ParsedQs, paramsToForward: string[]): string {
        const result = new URL(url);

        paramsToForward
            .filter((param) => Object.prototype.hasOwnProperty.call(existedParams, param))
            .forEach((param) => result.searchParams.append(param, existedParams[param] as string));

        return result.toString();
    }

    private forwardHeaders(existedHeaders, headersToForward: string[]): { [name: string]: string } {
        return headersToForward
            .filter((header) => existedHeaders[header] !== undefined)
            .reduce((accumulator, header) => {
                accumulator[header] = existedHeaders[header];
                return accumulator;
            }, {});
    }

    private isApiRequest(url: string) {
        return url.startsWith(this.environmentService.environment.apiOrigin);
    }
}
